import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { PageBanner } from '../components/PageBanner';
import { PageShell } from '@/components/PageShell';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { DataTablePagination, DataTableSkeleton, DataTableToolbar } from '@/components/DataTableControls';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserAvatar } from '@/components/UserAvatar';
import {
  Trophy,
  Medal,
  Award,
  Flame,
  Zap,
  TrendingUp,
  ChevronUp,
  ChevronDown,
  Crown,
  Star,
  User,
  BarChart3,
  ArrowUpRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RANK_STYLES = {
  1: { bg: 'bg-gradient-to-br from-yellow-400 to-amber-500', icon: Crown, color: 'text-yellow-600' },
  2: { bg: 'bg-gradient-to-br from-gray-300 to-gray-400', icon: Medal, color: 'text-gray-500' },
  3: { bg: 'bg-gradient-to-br from-amber-600 to-orange-700', icon: Award, color: 'text-amber-700' },
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

// EIP-215: canonical, deterministic leaderboard order. Points first, then current
// streak (matching the backend + report ordering), with user_id as a stable unique
// final key so tied users keep the same medal/rank between refreshes instead of
// relying on the fetch order.
const compareByRank = (a, b) =>
  (b.points - a.points) ||
  (b.current_streak - a.current_streak) ||
  String(a.user_id ?? '').localeCompare(String(b.user_id ?? ''));

export default function LeaderboardPage() {
  const { token, hasRole, user } = useAuth();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('points');
  const [sortOrder, setSortOrder] = useState('desc');
  const [timeRange, setTimeRange] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // EIP-234: approvers have read access to employee profiles (EIP-230), but the
  // stats summary endpoint stays admin/analyst-only on the backend — so the two
  // gates must differ or approver clicks silently no-op / summary fetches 403.
  const canViewEmployeeProfiles = hasRole(['admin', 'approver', 'analyst', 'superadmin']) || user?.is_superadmin;
  const canViewSummaryStats = hasRole(['admin', 'analyst', 'superadmin']) || user?.is_superadmin;

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await fetch(`${API_URL}/api/gamification/leaderboard?limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setLeaderboard(data.leaderboard || []);
        }
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchSummary = async () => {
      if (!canViewSummaryStats) {
        setSummary(null);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/employees/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSummary(data);
        }
      } catch (error) {
        console.error('Failed to fetch summary:', error);
      }
    };

    fetchLeaderboard();
    fetchSummary();
  }, [token, canViewSummaryStats]);

  const openEmployeeProfile = (userId) => {
    if (canViewEmployeeProfiles && userId) {
      navigate(`/people/employees/${userId}`);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const filteredAndSortedLeaderboard = useMemo(() => leaderboard
    .filter((entry) => {
      if (!searchQuery) return true;
      const searchLower = searchQuery.toLowerCase();
      return (
        entry.user_email?.toLowerCase().includes(searchLower) ||
        entry.name?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      const multiplier = sortOrder === 'desc' ? -1 : 1;
      let primary = 0;
      if (sortField === 'points') primary = a.points - b.points;
      else if (sortField === 'level') primary = a.level - b.level;
      else if (sortField === 'streak') primary = a.current_streak - b.current_streak;
      else if (sortField === 'badges') primary = a.badges_count - b.badges_count;
      // EIP-215: break ties with the canonical leaderboard order so the rank
      // column stays deterministic (and consistent with the podium) instead of
      // depending on the fetch order.
      return (multiplier * primary) || compareByRank(a, b);
    }), [leaderboard, searchQuery, sortField, sortOrder]);

  // Podium always shows the tenant-wide top 3 by points, unaffected by table search/sort
  const topThree = useMemo(
    () => [...leaderboard].sort(compareByRank).slice(0, 3),
    [leaderboard]
  );
  const totalPages = Math.max(Math.ceil(filteredAndSortedLeaderboard.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const pagedLeaderboard = filteredAndSortedLeaderboard.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const resetTablePage = () => setPage(1);

  const SortButton = ({ field, label }) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1 text-xs font-medium transition-colors ${
        sortField === field
          ? 'text-primary'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
      {sortField === field && (
        sortOrder === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />
      )}
    </button>
  );

  return (
    <PageShell maxWidth="7xl" data-testid="leaderboard-page">
      <PageBanner pageKey="engagement-leaderboard" />

      <PageHeader
        title="Leaderboard"
        className="mb-6"
        description="Track employee engagement and celebrate top performers"
      />

      {summary && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard title="Total Employees" value={summary.total_employees} icon={User} color="primary" />
          <KpiCard title="Engaged Users" value={summary.engaged_employees} icon={TrendingUp} color="success" />
          <KpiCard title="Active Today" value={summary.active_today} icon={Flame} color="warning" />
          <KpiCard title="Engagement Rate" value={`${summary.engagement_rate}%`} icon={BarChart3} color="info" />
        </div>
      )}

      {/* Top 3 Podium */}
      {!loading && topThree.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            Top Performers
          </h2>
          <div className="flex items-end justify-center gap-4 md:gap-8">
            {/* 2nd Place */}
            {topThree.length >= 2 && (
              <div
                className={`flex flex-col items-center ${canViewEmployeeProfiles ? 'cursor-pointer' : ''}`}
                data-testid="rank-2-card"
                onClick={() => openEmployeeProfile(topThree[1]?.user_id)}
              >
                <div
                  className={`w-20 h-20 md:w-24 md:h-24 rounded-full ${RANK_STYLES[2].bg} flex items-center justify-center shadow-lg mb-3`}
                >
                  <span className="text-2xl md:text-3xl font-bold text-white">2</span>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-foreground text-sm md:text-base">
                    {topThree[1]?.name || topThree[1]?.user_email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-muted-foreground">{topThree[1]?.points} pts</p>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <Zap className="w-3 h-3 text-yellow-500" />
                    <span className="text-xs text-muted-foreground">Lvl {topThree[1]?.level}</span>
                  </div>
                </div>
                <div className="w-20 md:w-24 h-24 md:h-32 bg-gray-300 dark:bg-gray-600 rounded-t-lg mt-3"></div>
              </div>
            )}

            {/* 1st Place */}
            <div
              className={`flex flex-col items-center ${canViewEmployeeProfiles ? 'cursor-pointer' : ''}`}
              data-testid="rank-1-card"
              onClick={() => openEmployeeProfile(topThree[0]?.user_id)}
            >
              <Crown className="w-8 h-8 text-yellow-500 mb-2 animate-bounce" />
              <div 
                className={`w-24 h-24 md:w-32 md:h-32 rounded-full ${RANK_STYLES[1].bg} flex items-center justify-center shadow-xl mb-3 ring-4 ring-yellow-300`}
              >
                <span className="text-3xl md:text-4xl font-bold text-white">1</span>
              </div>
              <div className="text-center">
                <p className="font-bold text-foreground text-base md:text-lg">
                  {topThree[0]?.name || topThree[0]?.user_email?.split('@')[0]}
                </p>
                <p className="text-sm text-muted-foreground">{topThree[0]?.points} pts</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm text-muted-foreground">Lvl {topThree[0]?.level}</span>
                </div>
              </div>
              <div className="w-24 md:w-32 h-32 md:h-40 bg-yellow-400 dark:bg-yellow-600 rounded-t-lg mt-3"></div>
            </div>

            {/* 3rd Place */}
            {topThree.length >= 3 && (
              <div
                className={`flex flex-col items-center ${canViewEmployeeProfiles ? 'cursor-pointer' : ''}`}
                data-testid="rank-3-card"
                onClick={() => openEmployeeProfile(topThree[2]?.user_id)}
              >
                <div
                  className={`w-20 h-20 md:w-24 md:h-24 rounded-full ${RANK_STYLES[3].bg} flex items-center justify-center shadow-lg mb-3`}
                >
                  <span className="text-2xl md:text-3xl font-bold text-white">3</span>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-foreground text-sm md:text-base">
                    {topThree[2]?.name || topThree[2]?.user_email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-muted-foreground">{topThree[2]?.points} pts</p>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <Zap className="w-3 h-3 text-yellow-500" />
                    <span className="text-xs text-muted-foreground">Lvl {topThree[2]?.level}</span>
                  </div>
                </div>
                <div className="w-20 md:w-24 h-16 md:h-24 bg-amber-600 dark:bg-amber-700 rounded-t-lg mt-3"></div>
              </div>
            )}
          </div>
        </div>
      )}

      <Card className="border-border bg-card shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">Leaderboard Workspace</CardTitle>
          <CardDescription>Search, sort, and inspect employee engagement rankings.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataTableToolbar
            searchValue={searchQuery}
            onSearchChange={(value) => {
              setSearchQuery(value);
              resetTablePage();
            }}
            searchPlaceholder="Search by name or email..."
            searchTestId="leaderboard-search"
            className="border-0 bg-transparent p-0"
          >
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-full sm:w-36" data-testid="time-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-input px-3 py-2">
              <SortButton field="points" label="Points" />
              <SortButton field="level" label="Level" />
              <SortButton field="streak" label="Streak" />
              <SortButton field="badges" label="Badges" />
            </div>
          </DataTableToolbar>

          {loading ? (
            <DataTableSkeleton
              rows={5}
              columns={5}
              showToolbar={false}
              data-testid="leaderboard-loading"
            />
          ) : filteredAndSortedLeaderboard.length === 0 ? (
            <EmptyState
              icon={Trophy}
              title="No engagement data yet"
              description="Leaderboard rankings will appear after employees start using Eva."
              variant="panel"
            />
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[900px] data-table" data-testid="leaderboard-table">
                  <thead className="bg-muted/50">
                    <tr className="border-b border-border">
                      <th className="w-16 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Employee</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Level</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Points</th>
                      <th className="hidden px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground md:table-cell">Streak</th>
                      <th className="hidden px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:table-cell">Badges</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedLeaderboard.map((entry, pageIndex) => {
                      const rank = (currentPage - 1) * pageSize + pageIndex + 1;
                      const isTopThree = rank <= 3;
                      const RankIcon = RANK_STYLES[rank]?.icon || Star;
                      
                      return (
                        <tr
                          key={entry.user_id}
                          className="cursor-pointer border-b border-border/50 transition-colors hover:bg-secondary/30"
                          data-testid={`leaderboard-row-${rank}`}
                          onClick={() => openEmployeeProfile(entry.user_id)}
                        >
                          <td className="px-4 py-4">
                            {isTopThree ? (
                              <div className={`w-8 h-8 rounded-full ${RANK_STYLES[rank].bg} flex items-center justify-center`}>
                                <RankIcon className="w-4 h-4 text-white" />
                              </div>
                            ) : (
                              <span className="text-lg font-semibold text-muted-foreground">#{rank}</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <UserAvatar
                                user={{ name: entry.name, email: entry.user_email, avatar_url: entry.avatar_url }}
                                size="md"
                              />
                              <div>
                                <p className="font-medium text-foreground">
                                  {entry.name || entry.user_email?.split('@')[0]}
                                </p>
                                <p className="max-w-[200px] truncate text-sm text-muted-foreground">{entry.user_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-primary">
                              <Zap className="w-3 h-3" />
                              <span className="text-sm font-semibold">{entry.level}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="font-bold text-foreground">{entry.points.toLocaleString()}</span>
                          </td>
                          <td className="hidden px-4 py-4 text-center md:table-cell">
                            <div className="inline-flex items-center gap-1 text-warning">
                              <Flame className="w-4 h-4" />
                              <span className="font-medium">{entry.current_streak}</span>
                            </div>
                          </td>
                          <td className="hidden px-4 py-4 text-center lg:table-cell">
                            <div className="inline-flex items-center gap-1 text-amber-600">
                              <Award className="w-4 h-4" />
                              <span className="font-medium">{entry.badges_count}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            {canViewEmployeeProfiles && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEmployeeProfile(entry.user_id);
                                }}
                                data-testid={`view-employee-${entry.user_id}`}
                              >
                                View
                                <ArrowUpRight className="w-3 h-3" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <DataTablePagination
                page={currentPage}
                totalPages={totalPages}
                totalItems={filteredAndSortedLeaderboard.length}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageChange={setPage}
                onPageSizeChange={(value) => { setPageSize(value); setPage(1); }}
                itemLabel="employees"
                pageSizeTestId="leaderboard-page-size"
              />
            </>
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
}
